import React, { useState, useEffect } from 'react';
import {
    Autocomplete, TextField, CircularProgress, Grid, Box, Typography
} from '@mui/material';
import axios from 'axios';

const GEO_API = 'https://geo.api.gouv.fr';
const FANTOIR_API = 'https://plateforme.adresse.data.gouv.fr/api-fantoir/communes';

/**
 * Formulaire d'adresse en cascade :
 * Département → Ville → Code postal → Rue
 *
 * Props :
 *   value     : { rue, ville, code_postal, pays }
 *   onChange  : (newValue) => void   — appelé à chaque modification
 *   required  : boolean
 *   spacing   : number (Grid spacing, défaut 2)
 */
export default function AdresseForm({ value = {}, onChange, required = false, spacing = 2 }) {
    const [departements, setDepartements] = useState([]);
    const [departement, setDepartement] = useState(null);
    const [loadingDept, setLoadingDept] = useState(false);

    const [villes, setVilles] = useState([]);
    const [ville, setVille] = useState(null);
    const [loadingVilles, setLoadingVilles] = useState(false);

    const [codesPostaux, setCodesPostaux] = useState([]);
    const [codePostal, setCodePostal] = useState(value.code_postal || null);

    const [rues, setRues] = useState([]);
    const [rue, setRue] = useState(value.rue || '');
    const [loadingRues, setLoadingRues] = useState(false);

    const set = (updates) => onChange?.({ ...value, ...updates });

    // ── 1. Charger tous les départements au montage ────────────────────────────
    useEffect(() => {
        setLoadingDept(true);
        axios.get(`${GEO_API}/departements?fields=nom,code&limit=200`)
            .then(r => setDepartements(r.data || []))
            .catch(() => {})
            .finally(() => setLoadingDept(false));
    }, []);

    // ── 2. Charger les villes quand le département change ─────────────────────
    useEffect(() => {
        if (!departement) { setVilles([]); setVille(null); return; }
        setLoadingVilles(true);
        setVille(null);
        setCodesPostaux([]);
        setCodePostal(null);
        setRues([]);
        setRue('');
        axios.get(`${GEO_API}/communes?codeDepartement=${departement.code}&fields=nom,code,codesPostaux&limit=500`)
            .then(r => {
                const sorted = (r.data || []).sort((a, b) => a.nom.localeCompare(b.nom));
                setVilles(sorted);
            })
            .catch(() => {})
            .finally(() => setLoadingVilles(false));
    }, [departement]);

    // ── 3. Extraire les codes postaux quand la ville change ───────────────────
    useEffect(() => {
        if (!ville) { setCodesPostaux([]); setCodePostal(null); setRues([]); setRue(''); return; }
        const codes = ville.codesPostaux || [];
        setCodesPostaux(codes);
        const firstCode = codes[0] || null;
        setCodePostal(firstCode);
        set({ ville: ville.nom, code_postal: firstCode || '', rue: '' });

        // Charger les rues pour cette ville (code INSEE)
        setLoadingRues(true);
        setRues([]);
        setRue('');
        axios.get(`${FANTOIR_API}/${ville.code}/voies`)
            .then(r => {
                const unique = [];
                (r.data || []).forEach(s => {
                    if (s.libelleVoieComplet && !unique.includes(s.libelleVoieComplet)) {
                        unique.push(s.libelleVoieComplet);
                    }
                });
                setRues(unique.sort());
            })
            .catch(() => {})
            .finally(() => setLoadingRues(false));
    }, [ville]);

    // ── 4. Mettre à jour le code postal ────────────────────────────────────────
    const handleCodePostal = (cp) => {
        setCodePostal(cp);
        set({ code_postal: cp || '' });
    };

    // ── 5. Mettre à jour la rue ────────────────────────────────────────────────
    const handleRue = (val) => {
        setRue(val);
        set({ rue: val || '' });
    };

    return (
        <Grid container spacing={spacing}>
            {/* Département */}
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    options={departements}
                    loading={loadingDept}
                    value={departement}
                    onChange={(_, v) => setDepartement(v)}
                    getOptionLabel={d => d ? `${d.code} – ${d.nom}` : ''}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderInput={(params) => (
                        <TextField {...params} label="Département" required={required}
                            slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loadingDept && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) } }} />
                    )}
                />
            </Grid>

            {/* Ville */}
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    options={villes}
                    loading={loadingVilles}
                    value={ville}
                    disabled={!departement}
                    onChange={(_, v) => setVille(v)}
                    getOptionLabel={v => v?.nom || ''}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderInput={(params) => (
                        <TextField {...params} label="Ville" required={required}
                            slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loadingVilles && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) } }} />
                    )}
                />
            </Grid>

            {/* Code postal */}
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    options={codesPostaux}
                    value={codePostal}
                    disabled={!ville}
                    onChange={(_, v) => handleCodePostal(v)}
                    getOptionLabel={v => v || ''}
                    renderInput={(params) => (
                        <TextField {...params} label="Code postal" required={required} />
                    )}
                />
            </Grid>

            {/* Rue */}
            <Grid item xs={12} sm={8}>
                {rues.length > 0 ? (
                    <Autocomplete
                        freeSolo
                        options={rues}
                        value={rue}
                        disabled={!ville}
                        loading={loadingRues}
                        onInputChange={(_, v) => handleRue(v)}
                        onChange={(_, v) => handleRue(v || '')}
                        renderOption={(props, opt) => {
                            const { key, ...rest } = props;
                            return <Box key={key} component="li" {...rest}><Typography variant="body2">{opt}</Typography></Box>;
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Rue" required={required}
                                slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loadingRues && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) } }} />
                        )}
                    />
                ) : (
                    <TextField
                        fullWidth
                        label="Rue"
                        required={required}
                        value={rue}
                        disabled={!ville}
                        onChange={e => handleRue(e.target.value)}
                        slotProps={{ input: { endAdornment: loadingRues ? <CircularProgress size={16} /> : null } }}
                    />
                )}
            </Grid>
        </Grid>
    );
}
